import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserContext } from "../contexts/UserContext";

function trim(v: unknown) {
    return String(v ?? "").trim();
}

type AdminCompanyLike = {
    tenantId?: unknown;
    companyId?: unknown;
    accountId?: unknown;
    companyName?: unknown;
    shopId?: unknown;
    shopUrl?: unknown;
    shopName?: unknown;
    plan?: unknown;
    trialStatus?: unknown;
};

type OptionRow = {
    tenantId: string;
    companyId: string;
    companyName: string;
    shopId: string;
    shopUrl: string;
    shopName: string;
    plan: string;
    trialStatus: string;
};

type Props = {
    companies?: AdminCompanyLike[];
};

function makeValue(companyId: string, shopUrl: string) {
    return `${companyId}__${shopUrl}`;
}

function buildLabel(o: OptionRow) {
    if (o.companyName && o.shopName && o.companyName !== o.shopName) {
        return `${o.companyName} / ${o.shopName}`;
    }
    if (o.shopName) return o.shopName;
    if (o.companyName) return o.companyName;
    return o.shopUrl;
}

export default function CompanySelector({ companies = [] }: Props) {
    const user = useUserContext();

    const impersonateCompanyId =
        (typeof window !== "undefined" && (
            localStorage.getItem("superImpersonateCompanyId") ||
            localStorage.getItem("impCompanyId") ||
            sessionStorage.getItem("superImpersonateCompanyId") ||
            sessionStorage.getItem("impCompanyId")
        )) || "";

    const isSuper = !!(
        (user as any)?.superAdmin ||
        (user as any)?.superAdminGlobal
    );

    const isImpersonating = !!trim(impersonateCompanyId);

    const options = useMemo<OptionRow[]>(() => {
        const propList: AdminCompanyLike[] = Array.isArray(companies) ? companies : [];
        const rawUser = (user as any)?.adminCompanies;
        const userList: AdminCompanyLike[] = Array.isArray(rawUser) ? rawUser : [];

        const source = propList.length ? propList : userList;

        const fallbackTenant =
            trim((user as any)?.tenantId) ||
            trim(localStorage.getItem("selectedTenant")) ||
            trim(sessionStorage.getItem("login_tenant")) ||
            "ecvision";

        return source
            .map((c) => ({
                tenantId: trim((c as any).tenantId) || fallbackTenant,
                companyId: trim((c as any).companyId) || trim((c as any).accountId),
                companyName: trim((c as any).companyName),
                shopId: trim((c as any).shopId),
                shopUrl: trim((c as any).shopUrl),
                shopName: trim((c as any).shopName),
                plan: trim((c as any).plan),
                trialStatus: trim((c as any).trialStatus),
            }))
            .filter((x) => x.companyId !== "" && x.shopUrl !== "")
            .sort((a, b) => {
                const an = `${a.companyName} ${a.shopName} ${a.shopUrl}`.normalize("NFKC");
                const bn = `${b.companyName} ${b.shopName} ${b.shopUrl}`.normalize("NFKC");
                return an.localeCompare(bn, "ja", { numeric: true, sensitivity: "base" });
            });
    }, [companies, user]);

    const canUseSelector = isSuper || isImpersonating || options.length > 1;

    const [selectedValue, setSelectedValue] = useState<string>(() => {
        if (typeof window === "undefined") return "";
        const companyId = trim(localStorage.getItem("selectedCompanyId") || localStorage.getItem("companyId"));
        const shopUrl = trim(localStorage.getItem("selectedShopUrl") || localStorage.getItem("shopUrl"));
        return companyId && shopUrl ? makeValue(companyId, shopUrl) : "";
    });

    const applyNormalSelection = useCallback((picked: OptionRow) => {
        localStorage.setItem("selectedTenant", picked.tenantId);
        sessionStorage.setItem("login_tenant", picked.tenantId);

        localStorage.setItem("selectedCompanyId", picked.companyId);
        localStorage.setItem("companyId", picked.companyId);
        localStorage.setItem("accountId", picked.companyId);

        localStorage.setItem("selectedShopUrl", picked.shopUrl);
        localStorage.setItem("shopUrl", picked.shopUrl);

        if (picked.plan) localStorage.setItem("plan", picked.plan);
        else localStorage.removeItem("plan");

        if (picked.trialStatus) localStorage.setItem("trialStatus", picked.trialStatus);
        else localStorage.removeItem("trialStatus");
    }, []);

    const applySuperSelection = useCallback((picked: OptionRow) => {
        localStorage.setItem("impCompanyId", picked.companyId);
        localStorage.setItem("impTenantId", picked.tenantId);
        localStorage.setItem("impShopUrl", picked.shopUrl);
        localStorage.setItem("superImpersonateCompanyId", picked.companyId);

        applyNormalSelection(picked);
    }, [applyNormalSelection]);

    useEffect(() => {
        if (!canUseSelector) return;
        if (!options.length) return;

        const currentCompanyId = trim(localStorage.getItem("selectedCompanyId") || localStorage.getItem("companyId"));
        const currentShopUrl = trim(localStorage.getItem("selectedShopUrl") || localStorage.getItem("shopUrl"));

        const picked =
            options.find((o) => o.companyId === currentCompanyId && o.shopUrl === currentShopUrl) ||
            options.find((o) => o.shopUrl === currentShopUrl) ||
            options[0];

        if (!picked) return;

        const nextValue = makeValue(picked.companyId, picked.shopUrl);

        // すでに同じ選択なら何もしない
        if (
            selectedValue === nextValue &&
            currentCompanyId === picked.companyId &&
            currentShopUrl === picked.shopUrl
        ) {
            return;
        }

        setSelectedValue(nextValue);

        if (isSuper || isImpersonating) {
            applySuperSelection(picked);
        } else {
            applyNormalSelection(picked);
        }

        const currentUserCompanyId = trim((user as any)?.companyId);
        if (currentUserCompanyId !== picked.companyId) {
            (user as any).applySelectedCompany?.({
                tenantId: picked.tenantId,
                companyId: picked.companyId,
                shopUrl: picked.shopUrl,
                plan: picked.plan,
                trialStatus: picked.trialStatus,
            });
        }
    }, [
        canUseSelector,
        options,
        selectedValue,
        isSuper,
        isImpersonating,
        applySuperSelection,
        applyNormalSelection,
        user,
    ]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = trim(e.target.value);
            if (!value) return;

            setSelectedValue(value);

            const [companyId, shopUrl] = value.split("__");
            const picked = options.find(
                (o) => o.companyId === trim(companyId) && o.shopUrl === trim(shopUrl)
            );
            if (!picked) return;

            if (isSuper || isImpersonating) {
                applySuperSelection(picked);
            } else {
                applyNormalSelection(picked);
            }

            (user as any).applySelectedCompany?.({
                tenantId: picked.tenantId,
                companyId: picked.companyId,
                shopUrl: picked.shopUrl,
                plan: picked.plan,
                trialStatus: picked.trialStatus,
            });
            window.dispatchEvent(new Event("company-selection-changed"));
        },
        [options, user, isSuper, isImpersonating, applySuperSelection, applyNormalSelection]
    );

    if (!canUseSelector) return null;
    if (!options.length) return null;

    return (
        <select
            value={selectedValue}
            onChange={handleChange}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            title="会社・店舗を選択"
        >
            {options.map((o) => (
                <option
                    key={`${o.tenantId}:${o.companyId}:${o.shopUrl}`}
                    value={makeValue(o.companyId, o.shopUrl)}
                >
                    {buildLabel(o)}
                </option>
            ))}
        </select>
    );
}